import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, ArrowLeft, Play, ListChecks, Clock, Phone, MessageSquare,
  Mail, Flag, ShieldCheck, Info, TrendingUp, Plus, X,
} from "lucide-react";

const CHANNEL_META = {
  call: { label: "Call", Icon: Phone, color: "text-blue-600" },
  text: { label: "Text", Icon: MessageSquare, color: "text-cyan-600" },
  email: { label: "Email", Icon: Mail, color: "text-violet-600" },
  final: { label: "Final", Icon: Flag, color: "text-rose-600" },
};

const DEFAULT_CADENCE = [
  { day: 0, channel: "call" }, { day: 2, channel: "text" }, { day: 5, channel: "email" }, { day: 9, channel: "final" },
];

const CadenceEditor = ({ steps, setSteps }) => {
  const upd = (i, patch) => setSteps(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <div className="mt-2 space-y-2" data-testid="cadence-editor">
      {steps.map((s, i) => {
        const M = CHANNEL_META[s.channel] || CHANNEL_META.text;
        return (
          <div key={i} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50/60 px-2.5 py-1.5" data-testid={`cadence-step-${i}`}>
            <M.Icon className={`h-4 w-4 shrink-0 ${M.color}`} />
            <span className="text-[12px] text-gray-500">Day</span>
            <Input type="number" min={0} max={90} value={s.day} onChange={(e) => upd(i, { day: e.target.value })}
              className="w-20 h-8 text-center" data-testid={`cadence-day-input-${i}`} />
            <Select value={s.channel} onValueChange={(v) => upd(i, { channel: v })}>
              <SelectTrigger className="w-[120px] h-8" data-testid={`cadence-channel-select-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CHANNEL_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <button className="ml-auto rounded p-1 text-gray-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30"
              disabled={steps.length <= 1} onClick={() => setSteps(steps.filter((_, j) => j !== i))} data-testid={`cadence-remove-${i}`}>
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
      <Button variant="outline" size="sm" disabled={steps.length >= 10} data-testid="cadence-add-step"
        onClick={() => setSteps([...steps, { day: (Number(steps[steps.length - 1]?.day) || 0) + 3, channel: "text" }])}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Add step
      </Button>
    </div>
  );
};

const fmtWhen = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SmartLeadFollowUpAgent() {
  const navigate = useNavigate();
  const [live, setLive] = useState(false);
  const [scheduleOn, setScheduleOn] = useState(true);
  const [suggested, setSuggested] = useState(20);
  const [hour, setHour] = useState("7");
  const [ampm, setAmPm] = useState("AM");
  const [safeList, setSafeList] = useState("");
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [cadence, setCadence] = useState(DEFAULT_CADENCE);
  const [maxAttempts, setMaxAttempts] = useState(5);
  const [sendRuns, setSendRuns] = useState([]);
  const [autoSend, setAutoSend] = useState(false);
  const [replyCheck, setReplyCheck] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/smart-leads/settings`);
        setLive(!!data.live_enabled || data.mode === "live");
        setScheduleOn(!!data.schedule_enabled);
        setSuggested(data.ai_suggested_count ?? 20);
        setSafeList((data.safe_list || []).join(", "));
        const [h, m] = (data.send_time || "07:00").split(":");
        let hh = parseInt(h, 10);
        setAmPm(hh >= 12 ? "PM" : "AM");
        setHour(String(((hh + 11) % 12) + 1));
        setLastRun(data.last_run_summary || null);
        setCadence(data.cadence?.length ? data.cadence : DEFAULT_CADENCE);
        setMaxAttempts(data.max_attempts ?? 5);
        setAutoSend(!!data.auto_send);
        setReplyCheck(data.last_reply_check_at ? `${new Date(data.last_reply_check_at).toLocaleString()} · ${data.last_reply_check_summary || ""}` : null);
      } catch (e) { /* defaults */ }
      try {
        const { data } = await axios.get(`${API}/smart-leads/send-runs`, { params: { limit: 8 } });
        setSendRuns(data.runs || []);
      } catch (e) { /* none yet */ }
    })();
  }, []);

  const save = async (nextLive) => {
    setSaving(true);
    try {
      let hh = parseInt(hour, 10) % 12;
      if (ampm === "PM") hh += 12;
      const live_enabled = nextLive !== undefined ? nextLive : live;
      await axios.post(`${API}/smart-leads/settings`, {
        mode: live_enabled ? "live" : "dry_run",
        ai_suggested_count: Number(suggested) || 20,
        send_time: `${String(hh).padStart(2, "0")}:00`,
        schedule_enabled: scheduleOn,
        live_enabled,
        safe_list: safeList.split(",").map((s) => s.trim()).filter(Boolean),
        cadence: cadence.map((c) => ({ day: parseInt(c.day, 10) || 0, channel: c.channel })),
        max_attempts: parseInt(maxAttempts, 10) || 5,
        auto_send: autoSend,
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally { setSaving(false); }
  };

  const checkReplies = async () => {
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/smart-leads/check-replies`);
      if (data.error) toast.error(`Reply check: ${data.error}`);
      else toast.success(`${data.checked} checked · ${data.replied.length} replied`);
      setReplyCheck(`${new Date().toLocaleString()} · ${data.checked} checked · ${data.replied.length} replied`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply check failed");
    } finally { setChecking(false); }
  };

  const runNow = async () => {
    setRunning(true);
    toast.info("Ranking this location's leads with AI… (no texts are sent)");
    try {
      const { data } = await axios.post(`${API}/smart-leads/run`);
      setLastRun(`${data.ranked} ranked · ${data.suggested} suggested`);
      toast.success(`Done — ${data.ranked} leads ranked, ${data.suggested} AI Suggested. Open the rep queue.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="smart-lead-agent-page">
      <Link to="/agents" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-cyan-700 transition-colors" data-testid="back-to-agents">
        <ArrowLeft className="h-4 w-4" /> All Agents
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Sparkles className="h-7 w-7 text-amber-500" />
            Smart Lead Follow-Up
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Ranks this location's open leads, tells reps who to contact now and why, and drafts the text. A person always reviews and sends — no auto-send.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge className={live ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"} data-testid="mode-badge">
            {live ? "Live armed" : "Dry run"}
          </Badge>
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-start gap-3" data-testid="mockup-note">
        <Info className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <b>Run now</b> ranks this location's open leads with AI. Reps review each drafted text in the queue; approved texts send through <b>Gingr</b> only when <b>Live</b> is armed (and only to the safe list while it's filled in). Leads not in Gingr are draft-only — reps text from their phone and log it. Every attempt advances the cadence; leads auto-close as <b>Lost</b> after the max attempts.
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={runNow} disabled={running} className="bg-gradient-to-r from-amber-500 to-orange-600" data-testid="run-now-btn">
          <Play className="h-4 w-4 mr-2" /> {running ? "Ranking…" : "Run now"}
        </Button>
        <Button variant="outline" onClick={() => navigate("/follow-up-queue")} className="border-cyan-300 text-cyan-700 hover:bg-cyan-50" data-testid="open-rep-queue-btn">
          <ListChecks className="h-4 w-4 mr-2" /> Open Follow-Up Queue
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Settings */}
        <Card className="border-gray-200 lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" />Agent settings</CardTitle>
            <CardDescription>Cadence, how many leads to surface, schedule, and safety — scoped to the location in the top bar.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-500">Follow-up cadence</Label>
              <CadenceEditor steps={cadence} setSteps={setCadence} />
              <p className="text-[11px] text-gray-400 mt-1.5">Days count from when the lead came in. Each logged attempt moves the lead to the next step; it re-surfaces as “Due today” on that day.</p>
              <div className="mt-3 flex items-center gap-2">
                <Label htmlFor="max-attempts" className="text-sm">Auto-close as Lost after</Label>
                <Input id="max-attempts" type="number" min={1} max={20} value={maxAttempts}
                  onChange={(e) => setMaxAttempts(e.target.value)} className="w-20 h-8 text-center" data-testid="max-attempts-input" />
                <span className="text-sm text-gray-600">unanswered attempts</span>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="suggested">“AI Suggested” per run</Label>
                <Input id="suggested" type="number" min={5} max={40} value={suggested}
                  onChange={(e) => setSuggested(e.target.value)} data-testid="suggested-count-input" />
                <p className="text-[11px] text-gray-400 mt-1">How many leads to mark “work these now” (15–25 recommended).</p>
              </div>
              <div>
                <Label>Daily run time</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input value={hour} onChange={(e) => setHour(e.target.value)} className="w-16 text-center" data-testid="schedule-hour" />
                  <button onClick={() => setAmPm((a) => (a === "AM" ? "PM" : "AM"))}
                    className="rounded-md border border-gray-200 px-3 py-2 text-sm font-medium hover:bg-gray-50" data-testid="schedule-ampm">{ampm}</button>
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-sm text-gray-600">Scheduled</span>
                    <Switch checked={scheduleOn} onCheckedChange={setScheduleOn} data-testid="schedule-switch" />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="safelist">Safe-list (while testing)</Label>
              <Input id="safelist" placeholder="Gingr owner IDs or phone numbers, comma-separated"
                value={safeList} onChange={(e) => setSafeList(e.target.value)} data-testid="safe-list-input" />
              <p className="text-[11px] text-gray-400 mt-1">When filled in, live sends ONLY go to these owners. Leave empty to allow every approved lead.</p>
            </div>

            <Separator />

            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <ShieldCheck className={`h-5 w-5 ${live ? "text-emerald-600" : "text-slate-400"}`} />
                <div>
                  <div className="text-sm font-semibold text-gray-800">{live ? "Live — rep-approved sends on" : "Dry run — nothing sends"}</div>
                  <div className="text-[11px] text-gray-500">Even live, every text needs a rep to approve. No auto-send, ever.</div>
                </div>
              </div>
              <Switch checked={live} onCheckedChange={(v) => { setLive(v); save(v); }} data-testid="arm-live-toggle" />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5">
              <div>
                <div className="text-sm font-semibold text-gray-800">Auto-send approved texts</div>
                <div className="text-[11px] text-gray-500">When on (and Live), texts reps add to the outbox fire automatically within ~5 min, 8am–8pm Mon–Sat. Off = reps hit “Send all”.</div>
              </div>
              <Switch checked={autoSend} onCheckedChange={setAutoSend} data-testid="auto-send-toggle" />
            </div>

            <div className="rounded-lg border border-rose-100 bg-rose-50/50 px-3 py-2.5 flex items-center gap-3" data-testid="reply-check-card">
              <div className="flex-1 text-[12px] text-rose-900">
                <b>Reply detection</b> — checks Gingr 2-way SMS every 2 hours (8am–8pm Mon–Sat) and right before every send. Leads who reply are paused and flagged for a rep.
                <div className="text-rose-700/80 mt-0.5" data-testid="reply-check-last">Last check: {replyCheck || "not yet"}</div>
              </div>
              <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 shrink-0" disabled={checking} onClick={checkReplies} data-testid="agent-check-replies-btn">
                {checking ? "Checking…" : "Check now"}
              </Button>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => save()} disabled={saving} data-testid="save-settings-btn" className="bg-gradient-to-r from-cyan-500 to-blue-600">
                {saving ? "Saving…" : "Save settings"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Impact + history */}
        <div className="space-y-5">
          <Card className="border-gray-200" data-testid="impact-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-600" />Last ranking</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="text-sm text-gray-700" data-testid="last-ranking-summary">{lastRun || "Not run yet"}</div>
              <div className="flex justify-between text-sm"><span className="text-gray-500">Sent via Gingr (recent)</span>
                <span className="font-semibold text-emerald-600" data-testid="sent-total">{sendRuns.reduce((a, r) => a + (r.sent || 0), 0)}</span></div>
            </CardContent>
          </Card>

          <Card className="border-gray-200" data-testid="run-history">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4 text-gray-400" />Send history</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {sendRuns.length === 0 && <div className="text-[12px] text-gray-400" data-testid="run-history-empty">No sends yet.</div>}
              {sendRuns.map((r, i) => (
                <div key={r.run_id} className="rounded-md bg-gray-50 px-2.5 py-1.5 text-[12px]" data-testid={`run-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">{fmtWhen(r.ran_at)}</span>
                    <Badge variant="outline" className="text-[10px]">{r.mode === "live" ? (r.status === "running" ? "Sending…" : "Live") : "Dry run"}</Badge>
                  </div>
                  <div className="text-gray-400 text-[11px] mt-0.5">
                    {r.mode === "live" ? `${r.sent} sent · ${r.failed} failed` : `${r.would_send} would send`} · {(r.skipped || []).length} skipped · by {r.by}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
